import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

export function PrivacyPolicy() {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [returnTo, setReturnTo] = useState(null);

  useEffect(() => {
    const checkoutReturnTo = localStorage.getItem('checkoutReturnTo');
    if (checkoutReturnTo) {
      setReturnTo(checkoutReturnTo);
    }
  }, []);

  const handleBackClick = () => {
    if (returnTo) {
      window.location.href = returnTo;
    } else {
      navigate(`/${language}/`);
    }
  };

  const backText = returnTo 
    ? (t('checkout.btnBack') || 'Back') + ' to checkout'
    : t('privacy.backToHome') || 'Back to home';

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <button 
          onClick={handleBackClick}
          className="text-primary hover:text-purple-400 transition-colors mb-8 inline-block cursor-pointer bg-transparent border-none"
        >
          ← {backText}
        </button>
        
        <h1 className="text-4xl font-heading font-bold text-white mb-8">
          {t('privacy.title')}
        </h1>

        <div className="prose prose-invert prose-slate max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('privacy.dataControllerTitle')}
            </h2>
            <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6">
              <p className="text-white font-semibold mb-2">
                {t('privacy.companyName')}
              </p>
              <p className="text-slate-400 mb-1">
                {t('privacy.nif')}
              </p>
              <p className="text-slate-400 mb-1">
                {t('privacy.address1')}
              </p>
              <p className="text-slate-400">
                {t('privacy.address2')}
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('privacy.dataCollectedTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              {t('privacy.dataCollectedText')}
            </p>
            <ul className="space-y-2 text-slate-300">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('privacy.contactDetails')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('privacy.projectInfo')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('privacy.commHistory')}</span>
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('privacy.purposeTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              {t('privacy.purposeText')}
            </p>
            <ul className="space-y-2 text-slate-300">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('privacy.serviceDelivery')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('privacy.clientComm')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('privacy.paymentProcessing')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('privacy.operationalImprovement')}</span>
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('privacy.paymentSecurityTitle')}
            </h2>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
              <p className="text-slate-300 mb-3">
                {t('privacy.paymentSecurityText1')}{' '}
                <span className="text-white font-semibold">Stripe</span>.
              </p>
              <p className="text-slate-400">
                {t('privacy.paymentSecurityText2')}
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('privacy.dataRightsTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              {t('privacy.dataRightsText')}
            </p>
            <ul className="space-y-2 text-slate-300">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('privacy.accessRight')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('privacy.rectificationRight')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('privacy.erasureRight')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>{t('privacy.restrictionRight')}</span>
              </li>
            </ul>
            <div className="mt-6 p-4 bg-slate-900/50 border border-slate-800 rounded-lg">
              <p className="text-slate-300">
                <span className="text-white font-semibold">
                  {t('privacy.requestsLabel')}
                </span>{' '}
                <a href="mailto:info@dathent.com" className="text-primary hover:text-purple-400 transition-colors">
                  info@dathent.com
                </a>
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('privacy.dataRetentionTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed">
              {t('privacy.dataRetentionText')}
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('privacy.thirdPartySharingTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed">
              {t('privacy.thirdPartySharingText')}
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-4">
              {t('privacy.contactTitle')}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              {t('privacy.contactText')}
            </p>
            <a href="mailto:info@dathent.com" className="text-primary hover:text-purple-400 transition-colors text-lg">
              info@dathent.com
            </a>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800">
          <p className="text-sm text-slate-500">
            {t('privacy.lastUpdated')}
          </p>
        </div>
      </div>
    </div>
  );
}
